import { useState, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authApi } from '../api/client';

const MIN_PASSWORD_LENGTH = 8;

function formatError(detail) {
  if (detail == null) return 'Er ging iets mis. Probeer het opnieuw.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => params.get('token') || '', [params]);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const tokenMissing = !token;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Wachtwoord moet minstens ${MIN_PASSWORD_LENGTH} tekens lang zijn.`);
      return;
    }
    if (password !== confirm) {
      setError('De wachtwoorden komen niet overeen.');
      return;
    }
    setLoading(true);
    try {
      await authApi.resetPassword({ token, password });
      setDone(true);
      // Bounce to login after a short pause so the user can read the success.
      setTimeout(() => navigate('/login'), 2200);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (tokenMissing) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="reset-password-page">
        <div className="w-full max-w-sm">
          <h1 className="font-serif text-3xl font-light text-center mb-3">Ongeldige link</h1>
          <div data-testid="reset-password-token-missing"
               className="text-sm bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg mb-4">
            Deze resetlink is onvolledig. Vraag een nieuwe link aan via Wachtwoord vergeten.
          </div>
          <Link
            to="/forgot-password"
            data-testid="reset-password-request-new-link"
            className="block text-center text-sm text-primary font-medium hover:underline"
          >
            Nieuwe link aanvragen
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="reset-password-page">
      <div className="w-full max-w-sm">
        <h1 className="font-serif text-3xl font-light text-center mb-3">Nieuw wachtwoord</h1>
        <p className="text-sm text-muted-foreground text-center mb-8">
          Kies een nieuw wachtwoord van minstens {MIN_PASSWORD_LENGTH} tekens.
        </p>

        {done ? (
          <div className="space-y-4" data-testid="reset-password-success">
            <div className="text-sm bg-primary/5 border border-primary/20 text-primary px-4 py-3 rounded-lg">
              Uw wachtwoord is opnieuw ingesteld. U wordt nu doorgestuurd naar de inlogpagina.
            </div>
            <Link
              to="/login"
              data-testid="reset-password-go-login"
              className="block text-center text-sm text-primary font-medium hover:underline"
            >
              Direct inloggen
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div data-testid="reset-password-error"
                   className="text-sm text-destructive bg-destructive/10 px-4 py-2.5 rounded-lg">
                {error}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Nieuw wachtwoord</label>
              <input
                data-testid="reset-password-new"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                minLength={MIN_PASSWORD_LENGTH}
                autoComplete="new-password"
                className="w-full px-4 py-2.5 rounded-lg border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Bevestig wachtwoord</label>
              <input
                data-testid="reset-password-confirm"
                type="password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                required
                minLength={MIN_PASSWORD_LENGTH}
                autoComplete="new-password"
                className="w-full px-4 py-2.5 rounded-lg border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <button
              data-testid="reset-password-submit"
              type="submit" disabled={loading}
              className="w-full bg-primary text-primary-foreground rounded-full py-3 font-semibold hover:bg-primary/90 transition-all active:scale-95 disabled:opacity-50"
            >
              {loading ? 'Bezig...' : 'Wachtwoord opslaan'}
            </button>
            <p className="text-center text-sm text-muted-foreground">
              <Link
                to="/login"
                data-testid="reset-password-cancel-link"
                className="text-primary font-medium hover:underline"
              >
                Terug naar inloggen
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
